import type { HarmonyPackage } from '@rhythmic/db/types'
import type { Transaction } from '@tanstack/vue-db'
import { defineStore, storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useRhythmicLogger } from '#imports'
import type { HarmonyItem } from '../../queries'
import useTeamContext from '../team/useTeamContext'
import type { CreateHarmonyPlanBoardPayload } from './useHarmonyPlanContext.createBoard'
import { handleCreateHarmonyPlanBoard } from './useHarmonyPlanContext.createBoard'
import type { CreateHarmonyPlanDocumentPayload } from './useHarmonyPlanContext.createDocument'
import { handleCreateHarmonyPlanDocument } from './useHarmonyPlanContext.createDocument'
import type { CreateHarmonyPlanFolderPayload } from './useHarmonyPlanContext.createFolder'
import { handleCreateHarmonyPlanFolder } from './useHarmonyPlanContext.createFolder'
import { handleDeleteHarmonyPlanBoard } from './useHarmonyPlanContext.deleteBoard'
import { handleDeleteHarmonyPlanDocument } from './useHarmonyPlanContext.deleteDocument'
import { handleDeleteHarmonyPlanFolder } from './useHarmonyPlanContext.deleteFolder'
import useHarmonyPlanContextQueries from './useHarmonyPlanContext.queries'
import type { UpdateHarmonyPlanBoardPayload } from './useHarmonyPlanContext.updateBoard'
import { handleUpdateHarmonyPlanBoard } from './useHarmonyPlanContext.updateBoard'
import type { UpdateHarmonyPlanDocumentPayload } from './useHarmonyPlanContext.updateDocument'
import { handleUpdateHarmonyPlanDocument } from './useHarmonyPlanContext.updateDocument'
import type { UpdateHarmonyPlanFolderPayload } from './useHarmonyPlanContext.updateFolder'
import { handleUpdateHarmonyPlanFolder } from './useHarmonyPlanContext.updateFolder'
import type { UpdateHarmonyPlanPayload } from './useHarmonyPlanContext.updatePlan'
import { handleUpdateHarmonyPlan } from './useHarmonyPlanContext.updatePlan'

export const useHarmonyPlanContext = defineStore('harmony-plan-context', () => {
  const logger = useRhythmicLogger('harmonyPlanContextStore')
  const teamContext = useTeamContext()
  const { harmonyPlan, harmonyPlanId, workspaceId: workspaceIdRef, team } = storeToRefs(teamContext)

  const queries = useHarmonyPlanContextQueries()
  queries.init({
    logger,
    harmonyPlanId,
    harmonyPlan,
    team,
    workspaceId: workspaceIdRef,
  })

  const teamId = computed<string | undefined>(() => team.value?.id ?? undefined)

  const isLoading = computed<boolean>(() => {
    return !harmonyPlan.value || queries.isLoading.value
  })

  const resolveIdentifiers = () => {
    const planId = harmonyPlan.value?.id ?? harmonyPlanId.value
    const currentTeamId = team.value?.id
    const workspaceId = workspaceIdRef.value

    if (!planId) throw new Error('Harmony plan context is not ready: missing plan identifier')

    if (!currentTeamId) throw new Error('Harmony plan context is not ready: missing team identifier')

    if (!workspaceId) throw new Error('Harmony plan context is not ready: missing workspace identifier')

    return { planId, teamId: currentTeamId, workspaceId }
  }

  async function createDocument(payload: CreateHarmonyPlanDocumentPayload) {
    const { planId, workspaceId } = resolveIdentifiers()

    return handleCreateHarmonyPlanDocument(payload, {
      planId,
      teamId: team.value!.id,
      workspaceId,
    })
  }

  async function updateDocument(documentId: string, payload: UpdateHarmonyPlanDocumentPayload) {
    const { planId, workspaceId } = resolveIdentifiers()

    return handleUpdateHarmonyPlanDocument(documentId, payload, {
      planId,
      workspaceId,
    })
  }

  function deleteDocument(documentId: string) {
    const { planId, teamId } = resolveIdentifiers()

    return handleDeleteHarmonyPlanDocument(documentId, {
      planId,
      workspaceId: workspaceIdRef.value!,
      teamId,
    })
  }

  async function deleteFolder(folderId: string) {
    const { planId, workspaceId } = resolveIdentifiers()

    return handleDeleteHarmonyPlanFolder(folderId, {
      planId,
      workspaceId,
    })
  }

  function createBoard(payload: CreateHarmonyPlanBoardPayload): Transaction {
    const { planId, workspaceId } = resolveIdentifiers()

    return handleCreateHarmonyPlanBoard(payload, {
      planId,
      workspaceId,
    })
  }

  function updateBoard(boardId: string, payload: UpdateHarmonyPlanBoardPayload): Transaction {
    const { planId } = resolveIdentifiers()

    return handleUpdateHarmonyPlanBoard(boardId, payload, {
      planId,
    })
  }

  async function deleteBoard(boardId: string) {
    const { planId, teamId } = resolveIdentifiers()

    return handleDeleteHarmonyPlanBoard(boardId, {
      planId,
      teamId,
    })
  }

  function createFolder(payload: CreateHarmonyPlanFolderPayload): Transaction {
    const { planId, workspaceId } = resolveIdentifiers()

    return handleCreateHarmonyPlanFolder(payload, {
      planId,
      workspaceId,
    })
  }

  function updateFolder(folderId: string, payload: UpdateHarmonyPlanFolderPayload): Transaction {
    const { planId } = resolveIdentifiers()

    return handleUpdateHarmonyPlanFolder(folderId, payload, {
      planId,
    })
  }

  function updatePlan(updates: UpdateHarmonyPlanPayload): Transaction {
    if (!queries.harmonyPackageCollection.value || !harmonyPlan.value)
      throw new Error('Harmony plan collection is not initialized')
    const { planId, teamId, workspaceId } = resolveIdentifiers()

    return handleUpdateHarmonyPlan(updates, {
      planId,
      teamId,
      workspaceId,
    })
  }

  return {
    queries,
    harmonyPlanId,
    teamId,
    workspaceId: workspaceIdRef,
    harmonyPlan,
    createDocument,
    updateDocument,
    deleteDocument,
    deleteFolder,
    createBoard,
    deleteBoard,
    updateBoard,
    createFolder,
    updateFolder,
    updatePlan,
    documents: queries.documents,
    boards: queries.boards,
    folders: queries.folders,
    harmonyWorkitems: queries.harmonyWorkitems,
    isLoading,
    isDocumentLoading: queries.isDocumentLoading,
    isBoardLoading: queries.isBoardLoading,
    isFolderLoading: queries.isFolderLoading,
  }
})

export default useHarmonyPlanContext
